import { OneUpHealthResponseEntry } from './MedicalDataTypes';
import { GetAvatarResponse } from './apiTypes/sharedTypes';
import { AuditLogMetadata, AuditLogMetadataWithPotentialOrganizationUser, UnresolvedAuditLog } from './AuditLogs';
import {
    FastenWebhookConnectionSuccessEvent,
    FastenWebhookAuthorizationRevokedEvent,
    FastenWebhookEhiExportSuccessEvent,
    FastenWebhookEhiExportFailedEvent,
} from './apiTypes/ehrIntegrationsService';
import * as APITypes from './apiTypes';
import * as MedicalDataTypes from './MedicalDataTypes';
import type * as MedicationChatTypes from './MedicationChatTypes';
import {
    ContactDetails,
    MedicalProfileFileExternalSourceEntityType,
    User,
    UserIntent,
    UserProductsAppStore,
} from './DynamoTypes';
import * as LLMTypes from './LLMTypes';
import {
    ReportCareTeamMembersInputData,
    ReportMedicalDataInputData,
    ReportObjectWithoutDerivedValues,
    ReportProfileInfoInputData,
} from './reports';
import { Dynamo, HubspotTypes, MedicalProfileFiles, NotificationTypes, Reports, SearchTypes } from '.';
import { AudioFileDetails, ImageDetails } from './apiTypes/medicalProfileFilesService';
import { GoogleChatMessage, GoogleChatSpaceName } from './GoogleWorkspaceTypes';
import { PageResponse, PageProperty, DatabaseName } from './NotionTypes';
import { FunctionType } from './FunctionType';
import { FeatureToggleName } from './FeatureToggleTypes';
import { OrganizationMedicalProfileCollaborator } from './MedicalProfileCollaborators';
import {
    Organization,
    OrganizationStripeAccountStatus,
    OrganizationInvoice,
    OrganizationInvoiceForStatus,
    OrganizationInvoiceItem,
} from './OrganizationTypes';
import {
    InAppNotification,
    MedicalProfileActivityCommentNotificationMetadata,
    MedicalProfileMessageNotificationMetadata,
    MedicalProfileNoteCommentNotificationMetadata,
    NotificationEvent,
    NotificationMetadata,
    NotificationType,
    ReadInAppNotification,
    ReadOrUnreadInAppNotification,
    UnreadInAppNotification,
} from './NotificationTypes';
import { ResourceImageCreate } from './apiTypes/medicalDataService';
import { OrganizationDetails, OrganizationInvoiceDetails } from './apiTypes/organizationService';
import { WebStripeClientType } from './apiTypes/paymentService';
import { StripeWebhookEventSource } from './WebhookTypes';
import { OneUpHealthEndpoint } from './apiTypes/ehrIntegrationsService';
import { MedicalProfileOrganizationCollaborator } from './apiTypes/medicalProfileService';

export type UserServicePostConfirmationArgs = {
    type: 'postConfirmation';
    email: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
    sub: string;
};

export type UserServicePostConfirmationReturnValue = {
    status: 'success' | 'error';
};

export type GetUserIDFromAPIGatewayInvocationArgs = {
    type: 'getUserIDFromAPIGatewayInvocation';
    userSub: string;
};

export type ResolveNewUserInvitesArgs = {
    type: 'resolveNewUserInvites';
    userId: string;
    userEmail: string;
};

export type ResolveNewUserTransferRequests = {
    type: 'resolveNewUserTransferRequests';
    userId: string;
    userEmail: string;
};

export type QueryOneUpHealthConnectedHealthSystemArgs = {
    type: 'queryOneUpHealthConnectedHealthSystem';
    connectedHealthSystemId: number;
};

export type GetPrimaryRecordNormalizedResourceArgs = {
    type: 'getPrimaryRecordNormalizedResource';
    resourceId: string;
};

export type GetPrimaryRecordNormalizedResourceReturnValue = {
    resource: MedicalDataTypes.PrimaryRecordResource | null;
};

export type QueryOneUpHealthConnectedHealthSystemReturnValue = {
    connectedHealthSystem: APITypes.ehrIntegrationsService.OneUpHealthEndpoint;
};

export type GetUserIDFromAPIGatewayInvocationReturnValue = {
    userId: string;
};

export type FileCommand<TType extends string, TTypeMetadata> = (
    | {
          type: TType;
          operation: 'delete';
      }
    | {
          type: TType;
          operation: 'upsert';
          tempFileId: string;
          /**
           * If provided, the temp file will be copied and not moved.  The S3 TTL will eventually delete the temp file.
           */
          keepTempFile?: boolean;
      }
    | {
          type: TType;
          operation: 'get';
      }
) &
    TTypeMetadata & {
        userId: string;
    };

export type FileCommandReturnValue = GetAvatarResponse | null;

export type MigrateResourceImage = {
    type: 'migrateResourceImage';
    medicalProfileId: string;
    oldResourceId: string;
    newResourceId: string;
    resourceImageId: string;
};

export type CopyFileCommand<TType extends string, TTypeSourceMetadata, TTypeDestinationMetadata = undefined> = {
    type: TType;
    operation: 'copy';
    sourceFile: TTypeSourceMetadata;
    destinationFile: TTypeDestinationMetadata extends undefined ? TTypeSourceMetadata : TTypeDestinationMetadata;
    userId: string;
};

export type CareTeamMemberAvatarFileMetadata = {
    medicalProfileId: string;
    careTeamMemberId: string;
};

export type CareTeamMemberAvatarFileCommand = FileCommand<'careTeamMemberAvatar', CareTeamMemberAvatarFileMetadata>;

export type OrganizationCareTeamMemberAvatarMetadata = {
    organizationId: string;
    organizationCareTeamMemberId: string;
};

export type OrganizationCareTeamMemberAvatarFileCommand = FileCommand<
    'organizationCareTeamMemberAvatar',
    OrganizationCareTeamMemberAvatarMetadata
>;

export type CopyCareTeamMemberAvatarFileCommand = CopyFileCommand<
    'copyCareTeamMemberAvatar',
    CareTeamMemberAvatarFileMetadata
>;

export type CopyOrganizationCareTeamMemberAvatarFileToCareTeamMemberAvatarFileCommand = CopyFileCommand<
    'copyOrganizationCareTeamMemberAvatarToCareTeamMemberAvatar',
    OrganizationCareTeamMemberAvatarMetadata,
    CareTeamMemberAvatarFileMetadata
>;

export type UserAvatarFileCommand = FileCommand<
    'userAvatar',
    {
        avatarUserId: string;
    }
>;

export type InitializeUserAvatarFromSocialProfile = {
    type: 'initializeUserAvatarFromSocialProfile';
    userId: string;
    avatarUrl: string;
};

export type MedicalProfileAvatarFileCommand = FileCommand<
    'medicalProfileAvatar',
    {
        medicalProfileId: string;
    }
>;

export type InsuranceCardImageFileMetadata = {
    medicalProfileId: string;
    careTeamMemberId: string;
    frontOrBack: 'front' | 'back';
};

export type InsuranceCardImageFileCommand = FileCommand<'insuranceCardImage', InsuranceCardImageFileMetadata>;

export type OrganizationCareTeamMemberInsuranceCardImageFileMetadata = {
    organizationId: string;
    organizationCareTeamMemberId: string;
    frontOrBack: 'front' | 'back';
};

export type OrganizationCareTeamMemberInsuranceCardImageFileCommand = FileCommand<
    'organizationCareTeamMemberInsuranceCardImage',
    OrganizationCareTeamMemberInsuranceCardImageFileMetadata
>;

export type CopyInsuranceCardImageFileCommand = CopyFileCommand<
    'copyInsuranceCardImage',
    InsuranceCardImageFileMetadata
>;

export type CopyOrganizationCareTeamMemberInsuranceCardImageFileToInsuranceCardImageFileCommand = CopyFileCommand<
    'copyOrganizationCareTeamMemberInsuranceCardImageToInsuranceCardImage',
    OrganizationCareTeamMemberInsuranceCardImageFileMetadata,
    InsuranceCardImageFileMetadata
>;

export type MedicalProfileInsuranceCardImageFileCommand = FileCommand<
    'medicalProfileInsuranceCardImage',
    {
        medicalProfileId: string;
        medicalProfileInsuranceCardId: string;
    }
>;

export type MedicalProfileIdCardImageFileCommand = FileCommand<
    'medicalProfileIdCardImage',
    {
        medicalProfileId: string;
        medicalProfileIdCardId: string;
    }
>;

export type ResourceImageFileCommand = FileCommand<
    'resourceImage',
    {
        medicalProfileId: string;
        resourceId: string;
        resourceImageId: string;
    }
>;

export type UserNoteFileCommand = FileCommand<
    'userNoteFile',
    {
        fileId: string;
    }
>;

export type ProfileNoteFileCommand = FileCommand<
    'profileNoteFile',
    {
        fileId: string;
        medicalProfileId: string;
    }
>;

export type OrganizationNoteFileCommand = FileCommand<
    'organizationNoteFile',
    {
        fileId: string;
        organizationId: string;
    }
>;

export type DeleteOrganizationNoteFilesCommand = {
    type: 'deleteOrganizationNoteFiles';
    organizationId: string;
};

export type DeleteOrganizationFilesCommand = {
    type: 'deleteOrganizationFiles';
    organizationId: string;
};

export type OrganizationInvitationFileCommand = FileCommand<
    'organizationInvitationFile',
    {
        fileId: string;
        organizationId: string;
    }
>;

export type CopyOrganizationInvitationFileCommand = {
    type: 'copyOrganizationInvitationFile';
    sourceFileId: string;
    copyFileId: string;
    organizationId: string;
};

export type GetOrganizationInvitationFileDownloadUrlCommand = {
    type: 'getOrganizationInvitationFileDownloadUrl';
    fileId: string;
    organizationId: string;
};

export type GetOrganizationInvitationFileDownloadUrlResponse = {
    downloadUrl: string;
};

export type CopyProfileNoteFileCommand = {
    type: 'copyProfileNoteFile';
    sourceFileId: string;
    sourceMedicalProfileId: string;
    destinationFileId: string;
    destinationMedicalProfileId: string;
};

export type CopyOrganizationNoteFileToProfileNoteFileCommand = {
    type: 'copyOrganizationNoteFileToProfileNoteFile';
    sourceFileId: string;
    sourceOrganizationId: string;
    destinationFileId: string;
    destinationMedicalProfileId: string;
};

export type CopyProfileNoteFileToOrganizationNoteFileCommand = {
    type: 'copyProfileNoteFileToOrganizationNoteFile';
    sourceFileId: string;
    sourceMedicalProfileId: string;
    destinationFileId: string;
    destinationOrganizationId: string;
};

export type CopyProfileNoteFileToOrganizationNoteFileResponse = {
    /** False when the source object no longer exists, in which case nothing was written. */
    copied: boolean;
};

export type MoveUserNoteFilesToProfileNoteFilesCommand = {
    type: 'moveUserNoteFilesToProfileNoteFiles';
    medicalProfileId: string;
    userId: string;
};

export type DeleteMedicalProfileFilesCommand = {
    type: 'deleteMedicalProfileFiles';
    medicalProfileId: string;
};

export type DeleteMedicalProfileFileArtifactsRequest = {
    type: 'deleteMedicalProfileFileArtifacts';
    medicalProfileId: string;
    medicalProfileFileId: string;
};

export type DeleteCareTeamMemberFilesCommand = {
    type: 'deleteCareTeamMemberFiles';
    medicalProfileId: string;
    careTeamMemberId: string;
};

export type DeleteOrganizationCareTeamMemberFilesCommand = {
    type: 'deleteOrganizationCareTeamMemberFiles';
    organizationId: string;
    organizationCareTeamMemberId: string;
};

export type DeleteMedicalProfileResourceData = {
    type: 'deleteMedicalProfileResourceData';
    medicalProfileId: string;
};

export type DeleteFastenDataForProfileCommand = {
    type: 'deleteFastenDataForProfile';
    medicalProfileId: string;
};

export type ClearTestUsersData = {
    type: 'clearTestUsersData';
    emails: string[];
};

export type ClearAllTestUsersData = {
    type: 'clearAllTestUsersData';
};

export type DeleteUserPaymentData = {
    type: 'deleteUserPaymentData';
    userId: string;
    /**
     * If true, will also delete the user's payment data from Stripe and Revenuecat as well
     * as entitlement history.
     *
     * This is likely only valid for test users.
     */
    deleteAllData: boolean;
};

export type SendInviteEmailParams = {
    first_name: string;
    last_name: string;
    sender_first_name: string | undefined;
    sender_last_name: string | undefined;
    profile_name: string;
};

export type SendInviteEmail = {
    type: 'sendInviteEmail';
    to: string;
    params: SendInviteEmailParams;
};

export type SendVerifyEmailEmailParams = {
    code: string;
};

export type SendVerifyEmailEmail = {
    type: 'sendVerifyEmailEmail';
    to: string;
    params: SendVerifyEmailEmailParams;
};

export type SendForgotPasswordEmailParams = {
    code: string;
};

export type SendForgotPasswordEmail = {
    type: 'sendForgotPasswordEmail';
    to: string;
    params: SendForgotPasswordEmailParams;
};

export type SendShareProfileEmailParams = {
    sender_first_name: string | undefined;
    sender_last_name: string | undefined;
    message: string | undefined;
    userStatus: 'existing' | 'new';
};

export type SendShareProfileEmail = {
    type: 'sendShareProfileEmail';
    to: string;
    params: SendShareProfileEmailParams;
};

export type SendTransferOwnershipEmailParams = {
    firstName: string | undefined;
    lastName: string | undefined;
    sender_first_name: string | undefined;
    sender_last_name: string | undefined;
    profile_name: string;
    userStatus: 'existing' | 'new' | 'collaborator';
    isPaymentRestricted: boolean;
    message: string | undefined;
};

export type SendTransferOwnershipEmail = {
    type: 'sendTransferOwnershipEmail';
    to: string;
    params: SendTransferOwnershipEmailParams;
};

export type InvitationLinkType = 'signup' | 'login';

export type SendOrganizationAgentInviteEmailParams = {
    firstName: string;
    lastName: string;
    sender_first_name: string;
    sender_last_name: string;
    sender_email: string;
    role: string;
    message: string;
    signupEmail: string;
    signupOrLoginUrl: string;
    linkType: InvitationLinkType;
};

export type SendOrganizationAgentInviteEmail = {
    type: 'sendOrganizationAgentInviteEmail';
    to: string;
    params: SendOrganizationAgentInviteEmailParams;
};

export type SendOrganizationClientInviteEmailParams = {
    firstName: string;
    lastName: string;
    sender_first_name: string;
    sender_last_name: string;
    sender_email: string;
    profileName: string;
    businessName: string;
    message: string;
    signupOrLoginUrl: string;
    linkType: InvitationLinkType;
    organizationLogoUrl: string;
};

export type SendOrganizationClientInviteEmail = {
    type: 'sendOrganizationClientInviteEmail';
    to: string;
    params: SendOrganizationClientInviteEmailParams;
};

export type OneUpHealthEntryWithSourceHealthSystem = OneUpHealthResponseEntry & {
    sourceHealthSystemId: number | null;
    sourceHealthSystemName: string | null;
    references: MedicalDataTypes.ResourceReferences | null;
};

export type PersistOneUpHealthResources = {
    type: 'persistOneUpHealthResources';
    medicalProfileId: string;
    refreshUserId: string;
    oneUpHealthResources: OneUpHealthEntryWithSourceHealthSystem[];
};

export type ReIngestEHRData = {
    type: 'reIngestEHRData';
    medicalProfileId: string;
    refreshUserId: string;
    createCompletionAuditLog?: boolean;
};

export type ReIngestAllEHRData = {
    type: 'reIngestAllEHRData';
};

export type DeleteMedicalProfile = {
    type: 'deleteMedicalProfile';
    medicalProfileId: string;
};

export type DeleteUserMedicalProfileData = {
    type: 'deleteUserMedicalProfileData';
    userId: string;
};

export type DeleteUserNotes = {
    type: 'deleteUserNotes';
    userId: string;
};

export type DeleteNote = {
    type: 'deleteNote';
    userId: string;
    noteId: string;
};

export type GetUserNoteAttachmentsDownloadUrls = {
    type: 'getUserNoteAttachmentsDownloadUrls';
    userId: string;
    medicalProfileId?: string;
    organizationId?: string;
    attachments: Dynamo.NoteFile[];
};

export type DeleteUserNoteFiles = {
    type: 'deleteUserNoteFiles';
    userId: string;
};

export type DeleteUserFiles = {
    type: 'deleteUserFiles';
    userId: string;
};

export type DeleteUser = {
    type: 'deleteUser';
    userId: string;
};

export type MigrateUserNoteFilesToMedicalProfile = {
    type: 'migrateUserNoteFilesToMedicalProfile';
    userId: string;
    medicalProfileId: string;
    fileIds: string[];
};

export type CreateAuditLog<TAuditLogMetadata extends AuditLogMetadata> = {
    type: 'createAuditLog';
    log: UnresolvedAuditLog<TAuditLogMetadata>;
};

export type CreateUserWelcomeNote = {
    type: 'createUserWelcomeNote';
    userId: string;
};

export type GetUserIdFromStripeCustomerIdRequest = {
    type: 'getUserIdFromStripeCustomerId';
    stripeCustomerId: string;
};

export type GetUserIdFromStripeCustomerIdResponse = {
    userId: string | null;
};

export type GetStripeProductTypeIdentifierRequest = {
    type: 'getStripeProductTypeIdentifier';
    productId: string;
};

export type GetStripeProductTypeIdentifierResponse = {
    productTypeIdentifier: APITypes.sharedTypes.ProductTypeIdentifiers;
};

export type RevenueCatEventType =
    | 'TEST'
    | 'INITIAL_PURCHASE'
    | 'NON_RENEWING_PURCHASE'
    | 'RENEWAL'
    | 'PRODUCT_CHANGE'
    | 'CANCELLATION'
    | 'BILLING_ISSUE'
    | 'SUBSCRIBER_ALIAS'
    | 'SUBSCRIPTION_PAUSED'
    | 'UNCANCELLATION'
    | 'TRANSFER'
    | 'SUBSCRIPTION_EXTENDED';

export type RevenueCatEvent = {
    userId: string;
    entitlementIds: APITypes.sharedTypes.Entitlement[];
    oldProductTypeIdentifiers?: APITypes.sharedTypes.ProductTypeIdentifiers[];
    productTypeIdentifiers: APITypes.sharedTypes.ProductTypeIdentifiers[];
    store: UserProductsAppStore;
    type: RevenueCatEventType;
    purchasedAt: number | null;
    expiresAt: number | null;
};

export type ProcessRevenueCatEventRequest = {
    type: 'processRevenueCatEvent';
    event: RevenueCatEvent;
};

export type BatchDeleteResourceRecordsRequest = {
    type: 'batchDeleteResourceRecords';
    userId: string;
    resourceRecordIdentifiers: {
        resourceId: string;
        medicalProfileId: string;
        resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    }[];
};

export type DeactivateMedicationRequest = {
    type: 'deactivateMedication';
    medicationId: string;
    medicalProfileId: string;
    expectedEndDateTime: string | null | undefined;
};

/** Scheduled send of visit invite emails for a visit created beyond the invite window. */
export type SendScheduledVisitInvitesRequest = {
    type: 'sendScheduledVisitInvites';
    medicalProfileId: string;
    resourceId: string;
};

export type OneUpHealthSystemResourceMetadata = {
    resourceRecordId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    createDateTime: string | null;
};

export type GetOneUpHealthSystemResourceMetadataRequest = {
    type: 'getOneUpHealthSystemResourceMetadata';
    medicalProfileId: string;
    healthSystemId: number;
};

export type GetOneUpHealthSystemResourceMetadataResponse = {
    resourceMetadata: OneUpHealthSystemResourceMetadata[];
};

// Fasten connection resource metadata types
export type FastenConnectionResourceMetadata = {
    resourceRecordId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    createDateTime: string | null;
};

export type GetFastenConnectionResourceMetadataRequest = {
    type: 'getFastenConnectionResourceMetadata';
    medicalProfileId: string;
    orgConnectionId: string;
};

export type GetFastenConnectionResourceMetadataResponse = {
    resourceMetadata: FastenConnectionResourceMetadata[];
};

export type UpdateRegistrationCodesRequest = {
    type: 'updateRegistrationCodes';
};

export type GetLLMChatCompletionRequest = {
    type: 'getLLMChatCompletion';
} & LLMTypes.ChatCompletionRequest;

export type GetLLMChatCompletionResponse = LLMTypes.ChatCompletionResponse;

export type GetLLMEmbeddingsRequest = {
    type: 'getLLMEmbeddings';
} & LLMTypes.EmbeddingsRequest;

export type GetLLMEmbeddingsResponse = LLMTypes.EmbeddingsResponse;

export type ActiveMedicationMedicalDataQuery = {
    type: 'active-medications';
};
export type AllMedicationMedicalDataQuery = {
    type: 'all-medications';
};
export type AllMedicalDataQuery = {
    type: 'all-medical-data';
};
export type ReportMedicalDataQuery =
    | ActiveMedicationMedicalDataQuery
    | AllMedicationMedicalDataQuery
    | AllMedicalDataQuery;
export type GetReportMedicalDataRequest = {
    type: 'getReportMedicalData';
    medicalProfileId: string;
    reportGenerationUserId: string;
    query: ReportMedicalDataQuery;
};

export type GetReportMedicalDataResponse = {
    reportMedicalData: ReportMedicalDataInputData;
};

export type GetReportProfileInformationRequest = {
    type: 'getReportProfileInformation';
    medicalProfileId: string;
    reportGenerationUserId: string;
};

export type GetReportCareTeamMemberDataRequest = {
    type: 'getReportCareTeamMemberData';
    medicalProfileId: string;
    reportGenerationUserId: string;
};

export type GetReportCareTeamMemberDataResponse = {
    reportCareTeamMembers: ReportCareTeamMembersInputData;
};

export type GetReportProfileInformationResponse = {
    reportProfileInfo: ReportObjectWithoutDerivedValues<ReportProfileInfoInputData>;
};

export type GenerateReportRequest = {
    type: 'generateReport';
    reportType: Reports.ReportType;
    medicalProfileId: string;
    generatorUserId: string;
};

export type PersistPDFMonkeyDocumentRequest = {
    type: 'persistPDFMonkeyDocument';
    userId: string;
    pdfMonkeyDownloadUrl: string;
};

export type PersistPDFMonkeyDocumentResponse = {
    tempFileId: string;
};

export type PersistMedicalProfileFileRequest = {
    type: 'persistMedicalProfileFile';
    uploadUserId: string;
    medicalProfileId: string;
    tempFileId: string;
    /**
     * Whether to delete the source file after persisting it, defaults to true
     */
    deleteSourceFile?: boolean;
};

export type PersistMedicalProfileFileResponse = {
    medicalProfileFileId: string;
};

export type CopyOrganizationInvitationFileToMedicalProfileRequest = {
    type: 'copyOrganizationInvitationFileToMedicalProfile';
    organizationId: string;
    sourceFileId: string;
    medicalProfileId: string;
};

export type CopyOrganizationInvitationFileToMedicalProfileResponse = {
    medicalProfileFileId: string;
};

export type GetOrganizationInvitationFileMetadataRequest = {
    type: 'getOrganizationInvitationFileMetadata';
    organizationId: string;
    invitationFileId: string;
};

type FileMetadataResponse = {
    contentType: string | null;
    contentLength: number | null;
    name: string | null;
};

export type GetOrganizationInvitationFileMetadataResponse = FileMetadataResponse;

export type PersistMedicalProfileFileFromNoteRequest = {
    type: 'persistMedicalProfileFileFromNote';
    medicalProfileId: string;
    noteFileId: string;
};

/**
 * Returns a presigned PUT URL for a medical profile's note-file S3 key, so a caller that already holds the bytes
 * (e.g. ehrIntegrationService with a decoded Fasten Binary document) can upload them straight to S3 without routing
 * the content through a Lambda invoke payload (which caps at 6MB). The same contentType MUST be used on the PUT.
 */
export type GetProfileNoteFileUploadUrlRequest = {
    type: 'getProfileNoteFileUploadUrl';
    medicalProfileId: string;
    noteFileId: string;
    contentType: string;
    name: string;
};

export type GetProfileNoteFileUploadUrlResponse = {
    uploadUrl: string;
};

/** Note-id prefix shared across services for system-generated Fasten clinical-document notes. */
export const FASTEN_DOCUMENT_NOTE_ID_PREFIX = 'fasten-docref-';

/**
 * Asks noteService (the owner of the Notes table) to create a system-generated clinical Note from a Fasten
 * document and attach the decoded document file — which fires the file-processing pipeline. The note is
 * created BEFORE processing starts so the async summary write-back can always find it.
 */
export type CreateFastenDocumentNoteRequest = {
    type: 'createFastenDocumentNote';
    medicalProfileId: string;
    createUserId: string;
    noteId: string;
    noteFileId: string;
    /** The Fasten DocumentReference id the note is generated from; stored as the note's provenance. */
    documentReferenceId: string;
    /** Resolved visit resourceId — the entityId for the 'visit-resource' note. */
    visitResourceId: string;
    title: string;
    fileName: string;
    contentType: string;
    /** Display label for the note creator at create time — the source portal (health system). */
    createdBySourceLabel?: string;
    /** Unix-ms date the document's content pertains to (the visit date) — sets the note's contentDate. */
    contentDate?: number;
};

/**
 * Asks noteService (the owner of the Notes table) to copy an organization note (title/content/tags/files)
 * onto a resource as a 'visit-resource' note — used to attach a note template when a visit is created.
 */
export type CopyNoteToResourceRequest = {
    type: 'copyNoteToResource';
    sourceNoteId: string;
    medicalProfileId: string;
    /** The created resource's id — the entityId of the new 'visit-resource' note. */
    resourceId: string;
    createUserId: string;
    /** When set, the source note must belong to this organization (org bulk flows). */
    organizationId?: string;
};

/** Asks noteService to write an AI summary into a Fasten clinical-document note once its file is processed. */
export type UpdateFastenDocumentNoteSummaryRequest = {
    type: 'updateFastenDocumentNoteSummary';
    noteId: string;
    summary: string;
};

/**
 * Asks noteService to create the client-record copy of one profile note for an organization, as a unit of the
 * backfill of copies each client record should already hold. The organization must be able to view the note under
 * the profile's rules today; a copy that already exists is left in place and, while it still tracks the note, given
 * any source comments it lacks. One note per call, so a call always fits the function's timeout.
 */
export type CreateClientRecordNoteMirrorRequest = {
    type: 'createClientRecordNoteMirror';
    noteId: string;
    organizationId: string;
    organizationClientRecordId: string;
    /** The organization member the copy is owned by and audited under. */
    actingUserId: string;
};

export type CreateClientRecordNoteMirrorResponse = {
    result: 'created' | 'existing' | 'not-found' | 'not-viewable';
    /** Comments added to a copy that already existed; a new copy takes the whole thread and is not counted here. */
    commentsCopied: number;
};

/** The last client record a backfill run finished before it stopped short of the job's timeout. */
export type BackfillClientRecordNoteMirrorsResumePoint = {
    organizationId: string;
    organizationClientRecordId: string;
};

/** Asks maintenanceJob to run the client-record copy backfill across every organization, one note at a time. */
export type BackfillClientRecordNoteMirrorsJobRequest = {
    type: 'backfillClientRecordNoteMirrors';
    includeCommentedNotes?: boolean;
    dryRun?: boolean;
    /** Only these organizations; the way to scope a run. */
    organizationIds?: string[];
    /** Continue a run that stopped short of the timeout: pass the `resumeAfter` it returned. */
    resumeAfter?: BackfillClientRecordNoteMirrorsResumePoint;
};

export type PersistMedicalProfileFileFromNoteResponse = {
    medicalProfileFileId: string;
};

export type PersistMedicalProfileFileFromMedicalProfileDirectiveFileRequest = {
    type: 'persistMedicalProfileFileFromMedicalProfileDirectiveFile';
    medicalProfileId: string;
    medicalProfileDirectiveFileId: string;
};

export type PersistMedicalProfileFileFromMedicalProfileDirectiveFileResponse = {
    medicalProfileFileId: string;
};

export type GetMedicalProfileNoteMetadataRequest = {
    type: 'getMedicalProfileNoteMetadata';
    medicalProfileId: string;
    noteFileId: string;
};

export type NoteFileMetadataResponse = FileMetadataResponse;

export type GetMedicalProfileNoteMetadataResponse = FileMetadataResponse;

export type GetUserNoteMetadataRequest = {
    type: 'getUserNoteMetadata';
    userId: string;
    noteFileId: string;
};

export type GetUserNoteMetadataResponse = FileMetadataResponse;

export type AttachmentInfo = {
    downloadUrl: string;
    name: string;
    contentType: string | undefined;
};

export type GetUserNoteAttachmentsDownloadUrlsResponse = {
    type: 'getUserNoteAttachmentsDownloadUrls';
    userNoteAttachmentsDownloadUrls: AttachmentInfo[];
};

export type GetOrganizationNoteMetadataRequest = {
    type: 'getOrganizationNoteMetadata';
    organizationId: string;
    noteFileId: string;
};

export type GetOrganizationNoteMetadataResponse = FileMetadataResponse;

export type GetMedicalProfileFileMetadataRequest = {
    type: 'getMedicalProfileFileMetadata';
    medicalProfileId: string;
    medicalProfileFileId: string;
};

export type GetMedicalProfileFileMetadataResponse = FileMetadataResponse;

export type CreateMedicalProfileFileFromSourceEntityFileRequest = {
    type: 'createMedicalProfileFileFromSourceEntityFile';
    createUserId: string;
    medicalProfileId: string;
    sourceEntityType: MedicalProfileFileExternalSourceEntityType;
    sourceEntityId: string;
    sourceFileId: string;
    fileName: string;
    contentType: string;
};

export type DeleteMedicalProfileFileForSourceEntityRequest = {
    type: 'deleteMedicalProfileFileForSourceEntity';
    medicalProfileId: string;
    sourceEntityType: MedicalProfileFileExternalSourceEntityType;
    sourceEntityId: string;
    sourceFileId: string;
};

export type DeleteMedicalProfileFileRequest = {
    type: 'deleteMedicalProfileFile';
    medicalProfileFileId: string;
    medicalProfileId: string;
};

export type GetMedicalProfileFilesBySourceEntityRequest = {
    type: 'getMedicalProfileFilesBySourceEntity';
    sourceEntityType: MedicalProfileFileExternalSourceEntityType;
    sourceEntityId: string;
    medicalProfileId: string;
};

export type GetMedicalProfileFilesBySourceEntityResponse = {
    medicalProfileFiles: APITypes.medicalProfileFilesService.MedicalProfileFileListItem[];
};

export type GetMedicalProfileFileIdsBySourceEntityRequest = {
    type: 'getMedicalProfileFileIdsBySourceEntity';
    sourceEntityType: MedicalProfileFileExternalSourceEntityType;
    sourceEntityId: string;
    medicalProfileId: string;
};

export type GetMedicalProfileFileIdsBySourceEntityResponse = {
    medicalProfileFileIds: string[];
};

export type GetMedicalProfileFileDownloadUrlRequest = {
    type: 'getMedicalProfileFileDownloadUrl';
    medicalProfileFileId: string;
    medicalProfileId: string;
};

export type GetMedicalProfileFileDownloadUrlResponse = {
    downloadUrl: string;
};

export type GetReportDocumentRequest = {
    type: 'getReportDocument';
    generatorUserId: string;
    tempFileId: string;
};

export type GetReportDocumentResponse = {
    reportUrl: string;
};

export type GetMedicalProfileMedicalDataRequest = {
    type: 'getMedicalProfileMedicalData';
    category?: MedicalDataTypes.PrimaryRecordResourceCategory;
    medicalProfileId: string;
};

export type GetMedicalProfileMedicalDataResponse = {
    resources: {
        normalizedResource: MedicalDataTypes.PrimaryRecordResource;
        relatedResourceIds: string[];
    }[];
};

export type GetMedicalProfileContactDetailsRequest = {
    type: 'getMedicalProfileContactDetails';
    medicalProfileId: string;
};

export type GetMedicalProfileContactDetailsResponse = ContactDetails;

export type ClearMedicalProfileNoKnownDrugAllergiesRequest = {
    type: 'clearMedicalProfileNoKnownDrugAllergies';
    medicalProfileId: string;
};

export type GetMedicalProfileCareTeamMembersRequest = {
    type: 'getMedicalProfileCareTeamMembers';
    medicalProfileId: string;
};

export type GetMedicalProfileCareTeamMembersResponse = {
    careTeamMembers: (Dynamo.CareTeamMember & {
        contactDetails?: ContactDetails;
    })[];
};

export type GetMedicalProfileCareTeamMembersByIdRequest = {
    type: 'getMedicalProfileCareTeamMembersById';
    medicalProfileId: string;
    careTeamMemberIds: string[];
};

export type GetMedicalProfileCareTeamMembersByIdResponse = {
    careTeamMembers: (Dynamo.CareTeamMember & {
        contactDetails?: ContactDetails;
    })[];
};

export type GetSearchClusterStatusRequest = {
    type: 'getSearchClusterStatus';
};

export type GetSearchClusterStatusResponse = SearchTypes.ClusterHealthStatus;

export type StartSearchBackfillStepFunctionRequest = {
    type: 'startSearchBackfillStepFunction';
    targets: SearchTypes.Target[];
};

export type InitiateSearchBackfillRequest = {
    type: 'initiateSearchBackfill';
    targets: SearchTypes.Target[];
};

export type InitiateSearchBackfillResponse = {
    dynamoExportArns: {
        source: SearchTypes.DynamoSource;
        arn: string;
    }[];
};

export type GetSearchBackfillExportStatusRequest = {
    type: 'getSearchBackfillExportStatus';
    dynamoExportArns: {
        source: SearchTypes.DynamoSource;
        arn: string;
    }[];
};

export type GetSearchBackfillExportStatusResponse =
    | {
          status: 'in-progress';
          // Step functions can't handle potentially undefined values, so we have to use null here
          manifests: null;
      }
    | {
          status: 'complete';
          manifests: {
              source: SearchTypes.DynamoSource;
              key: string;
          }[];
      }
    | {
          status: 'failed';
          manifests: null;
      };

export type DeleteSearchBackfillExportFilesRequest = {
    type: 'deleteSearchBackfillExportFiles';
    dynamoExportArns: {
        source: SearchTypes.DynamoSource;
        arn: string;
    }[];
};

export type ParseSearchBackfillExportManifestRequest = {
    type: 'parseSearchBackfillExportManifest';
    manifests: {
        source: SearchTypes.DynamoSource;
        key: string;
    }[];
};

export type ParseSearchBackfillExportManifestResponse = {
    source: SearchTypes.DynamoSource;
    dataFileKey: string;
    itemCount: number;
}[];

export type ProcessSearchBackfillExportDataFileRequest = {
    type: 'processSearchBackfillExportDataFile';
    dataFileKey: string;
    source: SearchTypes.DynamoSource;
    targets: SearchTypes.Target[];
    records: {
        offset: number;
        numberToProcess: number;
    };
};

export type ProcessSearchBackfillExportDataFileResponse = {
    recordsProcessed: number;
};

export type TransformResourceRecordsToSearchFieldsRequest = {
    type: 'transformResourceRecordsToSearchFields';
    resourceRecords: Dynamo.ResourceRecord[];
};

export type TransformResourceRecordsToSearchFieldsResponse = {
    transformedResourceRecords: SearchTypes.SearchTransformedResourceRecord[];
};

export type ExecuteSearchBulkOperationsRequest = {
    type: 'executeSearchBulkOperations';
    operations: SearchTypes.Operation[];
};

export type ExecuteSearchBackfillBulkOperationsRequest = {
    type: 'executeSearchBackfillBulkOperations';
    operations: SearchTypes.Operation[];
};

export type CreateSearchIndicesRequest = {
    type: 'createSearchIndices';
    targets: SearchTypes.Target[];
};

export type CreateSearchIndicesResponse = Record<SearchTypes.Target, string>;

export type ProcessSearchQueueMessagesRequest = {
    type: 'processSearchQueueMessages';
    numberToProcess: number;
};

export type ProcessSearchQueueMessagesResponse = {
    messagesProcessed: number;
};

export type UpdateSearchStateToRunningPendingUpdatesRequest = {
    type: 'updateSearchStateToRunningPendingUpdates';
};

export type UpdateSearchStateToValidatingPendingUpdatesRequest = {
    type: 'updateSearchStateToValidatingPendingUpdates';
};

export type UpdateSearchStateToPendingDeletionRequest = {
    type: 'updateSearchStateToPendingDeletion';
    targets: SearchTypes.Target[];
};

export type FinalizeSearchBackfillRequest = {
    type: 'finalizeSearchBackfill';
    targets: SearchTypes.Target[];
};

export type DeleteOldSearchIndicesRequest = {
    type: 'deleteOldSearchIndices';
    indexNames: string[];
};

export type UpsertSearchAliasesRequest = {
    type: 'upsertSearchAliases';
    aliases: {
        target: SearchTypes.Target;
        indexName: string;
    }[];
};

export type RefreshSearchGroupsRequest = {
    type: 'refreshSearchGroups';
    targets: SearchTypes.Target[];
    newIndices?: boolean;
};

export type DeleteUserSharedReportsRequest = {
    type: 'deleteUserSharedReports';
    userId: string;
};

export type GenerateSharedReportRequest = {
    type: 'generateSharedReport';
    shareCode: string;
    sourceIp: string | null;
    userAgent: string | null;
};

export type SharedReportNotFound = {
    status: 'NOT_FOUND';
};

export type SharedReportProcessing = {
    status: 'PROCESSING';
};

export type SharedReportReady = {
    status: 'COMPLETE';
    reportUrl: string;
};

export type GenerateSharedReportResponse = SharedReportNotFound | SharedReportProcessing | SharedReportReady;

/** Downloads the newest DB-IP City Lite release into the ip-location bucket (monthly cron + missing-database self-seed). */
export type RefreshIpLocationDatabaseRequest = {
    type: 'refreshIpLocationDatabase';
};

export type RefreshIpLocationDatabaseResponse = {
    /** `unchanged` when the stored database already came from the newest release. */
    status: 'updated' | 'unchanged';
    sourceUrl: string;
};

export type GenerateEmbeddingsRequest = {
    type: 'generateEmbeddings';
    text: string[];
};

export type GenerateEmbeddingsResponse = {
    embeddings: LLMTypes.Vector[];
};

export type ProcessFileRequest = {
    type: 'processFile';
    medicalProfileFileId: string;
};

export type FileProcessingPDFInitRequest = {
    type: 'fileProcessingPDFInit';
    medicalProfileFileId: string;
};

export type FileProcessingPDFInitResponse = {
    processArtifactId: string;
};

export type FileProcessingSplitPDFRequest = {
    type: 'fileProcessingSplitPDF';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingSplitPDFResponse = {
    processArtifactIds: string[];
};

export type FileProcessingImageInitRequest = {
    type: 'fileProcessingImageInit';
    medicalProfileFileId: string;
};

export type FileProcessingAudioInitRequest = {
    type: 'fileProcessingAudioInit';
    medicalProfileFileId: string;
};

export type FileProcessingAudioInitResponse = {
    processArtifactId: string;
};

export type FileProcessingImageInitResponse = {
    processArtifactIds: string[];
};

export type FileProcessingInitTranscriptionRequest = {
    type: 'fileProcessingInitTranscription';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingInitTranscriptionResponse = {
    processArtifactId: string;
    transcriptionJobId: string;
    status: 'processing';
};

export type FileProcessingPollForTranscriptionCompletionRequest = {
    type: 'fileProcessingPollForTranscriptionCompletion';
    medicalProfileFileId: string;
    processArtifactId: string;
    transcriptionJobId: string;
};

export type FileProcessingPersistTranscriptionResultRequest = {
    type: 'fileProcessingPersistTranscriptionResult';
    medicalProfileFileId: string;
    processArtifactId: string;
    transcriptionJobId: string;
};

export type FileProcessingPersistTranscriptionResultResponse = {
    processArtifactId: string;
};

export type FileProcessingGetAudioFileSummaryRequest = {
    type: 'fileProcessingGetAudioFileSummary';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingGetAudioFileSummaryResponse = {
    processArtifactId: string;
};

export type TranscriptionJobStatus = 'processing' | 'complete';

export type FileProcessingPollForTranscriptionCompletionResponse = {
    processArtifactId: string;
    status: TranscriptionJobStatus;
    transcriptionJobId: string;
};

export type FileProcessingExtractTextRequest = {
    type: 'fileProcessingExtractText';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingExtractTextResponse = {
    processArtifactId: string;
};

export type FileProcessingPersistTextRequest = {
    type: 'fileProcessingPersistText';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingPersistTextResponse = {
    processArtifactId: string;
};

export type FileProcessingGetPageSizeRequest = {
    type: 'fileProcessingGetPageSize';
    medicalProfileFileId: string;
    fileType: MedicalProfileFiles.FileProcessingType;
    processArtifactId: string;
};

export type FileProcessingGetPageSizeResponse = {
    processArtifactId: string;
};

export type FileProcessingGetPageRotationRequest = {
    type: 'fileProcessingGetPageRotation';
    medicalProfileFileId: string;
    fileType: MedicalProfileFiles.FileProcessingType;
    processArtifactId: string;
};

export type FileProcessingGetFileSummaryRequest = {
    type: 'fileProcessingGetFileSummary';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingGetFileSummaryResponse = {
    processArtifactId: string;
};

export type FileProcessingGetPageRotationResponse = {
    processArtifactId: string;
};

export type FileProcessingSectionOCRPageRequest = {
    type: 'fileProcessingSectionOCRPage';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingSectionOCRPageResponse = {
    processArtifactId: string;
};

export type FileProcessingSectionAudioRequest = {
    type: 'fileProcessingSectionAudio';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingSectionAudioResponse = {
    processArtifactId: string;
};

export type FileProcessingIdentifyAudioSpeakersRequest = {
    type: 'fileProcessingIdentifyAudioSpeakers';
    medicalProfileFileId: string;
    processArtifactId: string;
};

export type FileProcessingIdentifyAudioSpeakersResponse = {
    processArtifactId: string;
};

export type FileProcessingGetPageDescriptionRequest = {
    type: 'fileProcessingGetPageDescription';
    medicalProfileFileId: string;
    fileType: MedicalProfileFiles.FileProcessingType;
    processArtifactId: string;
};

export type FileProcessingGetPageDescriptionResponse = {
    processArtifactId: string;
};

export type FileProcessingProcessingComplete = {
    type: 'fileProcessingProcessingComplete';
    medicalProfileFileId: string;
    status: 'success' | 'failure';
};

export type CreateArtifactFileRequest = {
    type: 'createArtifactFile';
    medicalProfileId: string;
    medicalProfileFileId: string;
    artifactFileId: string;
    name: string;
    contentType: string;
};

export type CreateArtifactFileResponse = {
    uploadUrl: string;
};

export type GetArtifactFileDownloadUrlRequest = {
    type: 'getArtifactFileDownloadUrl';
    medicalProfileId: string;
    medicalProfileFileId: string;
    artifactFileId: string;
};

export type GetArtifactFileDownloadUrlResponse = {
    downloadUrl: string;
};

export type DeleteMedicalProfileFileArtifactRecordsRequest = {
    type: 'deleteMedicalProfileFileArtifactRecords';
    medicalProfileFileId: string;
};
export type StopMedicalProfileFileProcessingRequest = {
    type: 'stopMedicalProfileFileProcessing';
    medicalProfileFileId: string;
};
export type GetFileProcessedDetailsRequest = {
    type: 'getProcessedMedicalProfileFileDetails';
    medicalProfileFileId: string;
};

export type GetFileProcessedDetailsResponse = {
    summary: string | null;
    pageCount: number | null;
    detailedSummary: string | null;
    processingType: MedicalProfileFiles.FileProcessingType | null;
};

export type GetOneUpEndpointsDownloadUrlRequest = {
    type: 'getOneUpEndpointsDownloadUrl';
};

export type GetOneUpEndpointsDownloadUrlResponse = {
    downloadUrl: string | null;
};

export type GetOneUpEndpointsUploadUrlRequest = {
    type: 'getOneUpEndpointsUploadUrl';
};

export type GetOneUpEndpointsUploadUrlResponse = {
    uploadUrl: string;
};

export type GetAllOneUpEndpointsDownloadUrlRequest = {
    type: 'getAllOneUpEndpointsDownloadUrl';
};

export type GetAllOneUpEndpointsDownloadUrlResponse = {
    downloadUrl: string | null;
};

export type GetAllOneUpEndpointsUploadUrlRequest = {
    type: 'getAllOneUpEndpointsUploadUrl';
};

export type GetAllOneUpEndpointsUploadUrlResponse = {
    uploadUrl: string;
};

export type GetOneUpEndpointHistoricalUploadUrlRequest = {
    type: 'getOneUpEndpointHistoricalUploadUrl';
};

export type GetOneUpEndpointHistoricalUploadUrlResponse = {
    uploadUrl: string;
};

export type RefreshOneUpHealthEndpointsRequest = {
    type: 'refreshOneUpHealthEndpoints';
};

export type MedicalProfileFileQueryType = 'text' | 'fuzzy' | 'vector';

export type TextBasedMedicalProfileFileQuery = {
    searchType: 'text' | 'fuzzy';
    text: string;
};

export type VectorBasedMedicalProfileFileQuery = {
    searchType: 'vector';
    searchVector: LLMTypes.Vector;
    k: number;
};

export type QueryMedicalProfileFilesRequest = {
    type: 'queryMedicalProfileFiles';
    medicalProfileId: string;
    includeMedicalProfileFileIds: string[];
} & (TextBasedMedicalProfileFileQuery | VectorBasedMedicalProfileFileQuery);

export type QueryMedicalProfileFilesResponse = {
    medicalProfileFileArtifactIds: string[];
};

export type QueryMedicalProfileFileAndNoteVectorsRequest = {
    type: 'queryMedicalProfileFileAndNoteVectors';
    medicalProfileId: string;
    searchVector: LLMTypes.Vector;
    k: number;
    includeNoteEntityTypes: Dynamo.NoteEntityType[];
    includeMedicalProfileFileIds: string[];
};

// Used when user does not have access to files
export type QueryMedicalProfileNoteVectorsRequest = {
    type: 'queryMedicalProfileNoteVectors';
    medicalProfileId: string;
    searchVector: LLMTypes.Vector;
    k: number;
    includeNoteEntityTypes: Dynamo.NoteEntityType[];
};

export type QueryMedicalProfileFileAndNoteVectorsResponse = {
    ids: {
        id: string;
        type: 'ocr-section' | 'audio-section' | 'page-description' | 'note';
    }[];
};

export type QueryMedicalProfileNoteVectorsResponse = {
    ids: {
        id: string;
        type: 'note';
    }[];
};

export type ResolveMedicalProfileFileArtifactSearchRequest = {
    type: 'resolveMedicalProfileFileArtifactSearch';
    medicalProfileId: string;
    artifactIds: string[];
};

export type ResolveImageBasedSearchItemProps = {
    type: 'image';
    text?: string;
    page: number;
    boundingBox?: MedicalProfileFiles.BoundingBox | null;
};

export type ResolveAudioBasedSearchItemProps = {
    type: 'audio';
    text: string;
    page: number;
    startTime: number;
    endTime: number;
    speakerIndex: number;
};

type BasedMedicalProfileFileArtifactSearchItem = {
    medicalProfileFileArtifactId: string;
    medicalProfileFileId: string;
};

export type ImageBasedSearchItem = BasedMedicalProfileFileArtifactSearchItem & ResolveImageBasedSearchItemProps;
export type AudioBasedSearchItem = BasedMedicalProfileFileArtifactSearchItem & ResolveAudioBasedSearchItemProps;

export type ResolvedMedicalProfileFileArtifactSearchItem = ImageBasedSearchItem | AudioBasedSearchItem;

export type ResolveMedicalProfileFileArtifactSearchResponse = {
    resolvedArtifacts: ResolvedMedicalProfileFileArtifactSearchItem[];
};

export type LoadPageDerivedMedicalProfileFileArtifactRequest = {
    type: 'loadPageDerivedMedicalProfileFileArtifact';
    medicalProfileFileId: string;
    page: number;
    artifactType: MedicalProfileFiles.MedicalProfileFileArtifact['artifactType'];
};

export type LoadPageDerivedMedicalProfileFileArtifactResponse = {
    artifact: MedicalProfileFiles.MedicalProfileFileArtifact;
};

export type LoadMedicalProfileFileArtifactsForMedicalProfileFileRequest = {
    type: 'loadMedicalProfileFileArtifactsForMedicalProfileFile';
    medicalProfileFileId: string;
    artifactTypes?: MedicalProfileFiles.MedicalProfileFileArtifact['artifactType'][];
};

export type LoadMedicalProfileFileArtifactsForMedicalProfileFileResponse = {
    artifacts: MedicalProfileFiles.MedicalProfileFileArtifact[];
};

export type GetMedicalProfileFileArtifactsByIdRequest = {
    type: 'getMedicalProfileFileArtifactsById';
    medicalProfileFileArtifactIds: string[];
};

export type GetMedicalProfileFileArtifactsByIdResponse = {
    artifacts: MedicalProfileFiles.MedicalProfileFileArtifact[];
};

export type GetMedicalProfileNotesByIdRequest = {
    type: 'getMedicalProfileNotesById';
    medicalProfileId: string;
    noteIds: string[];
};

export type GetMedicalProfileNotesByIdResponse = {
    notes: Dynamo.WithTimestamps<Dynamo.Note>[];
};

export type DeleteMedicalProfileFileProcessingDataRequest = {
    type: 'deleteMedicalProfileFileProcessingRecords';
    medicalProfileId: string;
    medicalProfileFileId: string;
};

export type QueryMedicalProfileResourceVectorsRequest = {
    type: 'queryMedicalProfileResourceVectors';
    medicalProfileId: string;
    searchVector: LLMTypes.Vector;
    resourceCategories: MedicalDataTypes.PrimaryRecordResourceCategory[];
    k: number;
    primaryOrUniqueResourcesOnly?: boolean;
};

export type GetMedicationGroupCandidatesRequest = {
    type: 'getMedicationGroupCandidates';
    medicalProfileId: string;
    resourceIds: string[];
};
export type GetMedicationGroupCandidatesResponse = MedicationChatTypes.GroupSearchResult;

export type GetMedicationChatEvidenceRequest = {
    type: 'getMedicationChatEvidence';
    medicalProfileId: string;
    userId: string;
    resourceIds: string[];
};
export type GetMedicationChatEvidenceResponse = {
    records: MedicationChatTypes.MedicationEvidence[];
    missingResourceIds: string[];
};

export type QueryMedicalProfileResourceVectorsResponse = {
    resourceId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
}[];

export type QueryMedicalProfileCareTeamMemberVectorsRequest = {
    type: 'queryMedicalProfileCareTeamMemberVectors';
    medicalProfileId: string;
    searchVector: LLMTypes.Vector;
    k: number;
};

export type QueryMedicalProfileCareTeamMemberVectorsResponse = {
    careTeamMemberIds: string[];
};

export type GetMedicalProfileFilePageImageRequest = {
    type: 'getMedicalProfileFilePageImage';
    medicalProfileFileId: string;
    page: number;
};

export type GetMedicalProfileFilePageImageResponse = {
    image: ImageDetails | null;
};

export type GetMedicalProfileFileAudioRequest = {
    type: 'getMedicalProfileFileAudio';
    medicalProfileFileId: string;
};

export type GetMedicalProfileFileAudioResponse = {
    audio: AudioFileDetails | null;
};

export type QueryResourceCountsRequest = {
    type: 'queryResourceCounts';
    medicalProfileId: string;
};

export type QueryResourceCountsResponse = {
    totals: APITypes.medicalDataService.TotalCounts;
    filtered: APITypes.medicalDataService.FilteredCounts;
};

export type SendGoogleChatMessageRequest = {
    type: 'sendGoogleChatMessage';
    spaceName: GoogleChatSpaceName;
    message: GoogleChatMessage;
};

export type CreateHubspotContactFromUserRequest = {
    type: 'createHubspotContactFromUser';
    user: Dynamo.User;
    userIdLookup: Dynamo.UserIdLookup;
    allUsedRegistrationCodes: string[];
    hasActiveRegistrationCode: boolean;
};

export type CreateHubspotContactFromUserResponse = {
    contactId: string;
};

export type CreateHubspotCompanyFromOrganizationRequest = {
    type: 'createHubspotCompanyFromOrganization';
    organization: Dynamo.Organization;
    contactDetails: ContactDetails;
};

export type CreateHubspotCompanyFromOrganizationResponse = {
    companyId: string;
};

export type PatchHubspotContactByEmailRequest = {
    type: 'patchHubspotContactByEmail';
    emailAddress: string;
    properties: Partial<HubspotTypes.ContactProperties>;
    associations?: HubspotTypes.Association[];
};

export type PatchHubspotCompanyByIdRequest = {
    type: 'patchHubspotCompanyById';
    companyId: string;
    properties: Partial<HubspotTypes.CompanyProperties>;
};

export type RunHubspotBackfillRequest = {
    type: 'runHubspotBackfill';
    dryRun?: boolean;
};

export type RunHubspotBackfillResponse = {
    dryRun: boolean;
    anonymousContactsFound: number;
    contactsPatched: number;
    contactsSkipped: number;
};

export type GetHubspotContactUTMParametersByEmailRequest = {
    type: 'getHubspotContactUTMParametersByEmail';
    emailAddress: string;
};

export type GetHubspotContactUTMParametersByEmailResponse = {
    utmParameters: HubspotTypes.UTMParameters | null;
};

export type GetRelationshipsForMedicalProfileResourcesRequest = {
    type: 'getRelationshipsForMedicalProfileResources';
    medicalProfileId: string;
    resourceIds: string[];
};

type MedicalProfileResourceRelationships = {
    resourceId: string;
    /**
     * Resources where the given resource is the target resource
     */
    targetResourceIds: string[];
    /**
     * Resources where the given resource is the source resource
     */
    sourceResourceIds: string[];
};

export type GetRelationshipsForMedicalProfileResourcesResponse = {
    relationships: MedicalProfileResourceRelationships[];
};

export type GetMedicalProfileResourcesByIdRequest = {
    type: 'getMedicalProfileResourcesById';
    medicalProfileId: string;
    resourceIds: string[];
};

export type GetMedicalProfileResourcesByIdResponse = {
    resources: {
        normalizedResource: MedicalDataTypes.PrimaryRecordResource;
        relatedResourceIds: string[];
    }[];
};

export type GetNotionDatabasePagesRequest = {
    type: 'getNotionDatabasePages';
    databaseName: DatabaseName;
};

export type GetNotionDatabasePagesResponse = {
    pages: PageResponse[];
} | null;

export type ArchiveNotionPageRequest = {
    type: 'archiveNotionPage';
    pageId: string;
};

export type PatchNotionPagePropertiesRequest = {
    type: 'patchNotionPageProperties';
    pageId: string;
    properties: PageProperty[];
};

export type CreateNotionPageRequest = {
    type: 'createNotionPage';
    properties: PageProperty[];
    parentDatabaseName: DatabaseName;
};

export type CreateNotionPageResponse = {
    pageId: string;
};

export type RefreshFeatureFlagsNotionDatabasesRequest = {
    type: 'refreshFeatureFlagsNotionDatabases';
};

export type GetLabDateBucketsRequest = {
    type: 'getLabDateBuckets';
    medicalProfileId: string;
};

export type GetLabDateBucketsResponse = APITypes.medicalDataService.GetLabDateBucketResponse;

export type GetLabIdsForDateRequest = {
    type: 'getLabIdsForDate';
    medicalProfileId: string;
    dateTime: string;
};

export type GetLabIdsForDateResponse = {
    labIds: string[];
};

export type GetLabIdsForCodeRequest = {
    type: 'getLabIdsForCode';
    medicalProfileId: string;
    code: string;
};

export type GetLabIdsForCodeResponse = {
    labIds: string[];
};

export type GetUniqueLabCodesRequest = {
    type: 'getUniqueLabCodes';
    medicalProfileId: string;
};

export type GetUniqueLabCodesResponse = APITypes.medicalDataService.GetUniqueLabCodesResponse;

/**
 * Create a scheduled lambda invocation
 *
 * Make sure that jobSchedulerService has IAM permission to invoke the supplied lambda function
 */
export type CreateScheduledLambdaInvocationRequest<TInvocationType> = {
    type: 'createScheduledLambdaInvocation';
    lambdaFunctionName: FunctionType;
    invocationDateTime: number;
    payload: TInvocationType;
};

export type RunScheduledJobsRequest = {
    type: 'runScheduledJobs';
    jobType: Dynamo.ScheduledJobType;
};

export type ApplyDefaultRegistrationCodeRequest = {
    type: 'applyDefaultRegistrationCode';
    userId: string;
    userEmail: string;
};

export type GetFeatureTogglesForUserRequest = {
    type: 'getFeatureTogglesForUser';
    userId: string;
};

export type GetFeatureTogglesForUserResponse = Partial<Record<FeatureToggleName, boolean>>;

export type ResolveMedicalProfileFileArtifactChatReferencesRequest = {
    type: 'resolveMedicalProfileFileArtifactChatReferences';
    medicalProfileId: string;
    medicalProfileFileArtifactIds: string[];
};

export type ResolveMedicalProfileFileArtifactSearchReferencesRequest = {
    type: 'resolveMedicalProfileFileArtifactSearchReferences';
    medicalProfileId: string;
    medicalProfileFileArtifactIds: string[];
};

type ResolvedReferencedPageFileArtifactSpecificField = {
    type: 'page';
    page: number;
};

type ResolvedReferencedAudioFileArtifactSpecificFields = {
    type: 'audio';
    page: number;
    startTime: number;
    endTime: number;
    speakerIndex: number;
    text: string;
};

export type BaseResolvedReferencedFileArtifact = {
    category: 'file-artifact';
    medicalProfileId: string;
    medicalProfileFileId: string;
    medicalProfileFileArtifactId: string;
};

export type ResolvedReferencedPageFileArtifact = ResolvedReferencedPageFileArtifactSpecificField &
    BaseResolvedReferencedFileArtifact;

export type ResolvedReferencedAudioFileArtifact = ResolvedReferencedAudioFileArtifactSpecificFields &
    BaseResolvedReferencedFileArtifact;

export type ResolvedChatReferencedMedicalProfileFileArtifact =
    | ResolvedReferencedPageFileArtifact
    | ResolvedReferencedAudioFileArtifact;

type ResolvedReferencedImageFileArtifactSpecificFields = {
    type: 'image';
    page: number;
    text?: string;
    boundingBox?: MedicalProfileFiles.BoundingBox | null;
};

export type ResolvedReferencedImageFileArtifact = ResolvedReferencedImageFileArtifactSpecificFields &
    BaseResolvedReferencedFileArtifact;

export type ResolvedSearchReferencedMedicalProfileFileArtifact =
    | ResolvedReferencedPageFileArtifact
    | ResolvedReferencedAudioFileArtifact
    | ResolvedReferencedImageFileArtifact;

export type ResolveMedicalProfileFileArtifactChatReferencesResponse = {
    resolvedArtifacts: ResolvedChatReferencedMedicalProfileFileArtifact[];
};

export type ResolveMedicalProfileFileArtifactSearchReferencesResponse = {
    resolvedArtifacts: ResolvedSearchReferencedMedicalProfileFileArtifact[];
};

export type GetUserRegistrationCodeActivityRequest = {
    type: 'getUserRegistrationCodeActivity';
    userId: string;
};

export type GetUserRegistrationCodeActivityResponse = {
    allUsedRegistrationCodes: string[];
    hasActiveRegistrationCode: boolean;
};

export type GetAllAllowedMedicalProfileFileIdsForUserRequest = {
    type: 'getAllAllowedMedicalProfileFileIdsForUser';
    medicalProfileId: string;
    userId: string;
};

export type GetAllAllowedMedicalProfileFileIdsForUserResponse = {
    medicalProfileFileIds: string[];
};

export type GetMedicalProfileNoteIdsForEntityTypesRequest = {
    type: 'getMedicalProfileNoteIdsForEntityTypes';
    medicalProfileId: string;
    entityTypes: Dynamo.NoteEntityType[];
};

export type GetMedicalProfileNoteIdsForEntityTypesResponse = {
    noteIds: string[];
};

export type MaybeDeleteUserOrganizationResourcesRequest = {
    type: 'maybeDeleteUserOrganizationResources';
    userId: string;
};

export type ResolveNewUserOrganizationInvitationsRequest = {
    type: 'resolveNewUserOrganizationInvitations';
    userEmail: string;
    userId: string;
};

export type GetDoesEmailHavePendingOrganizationUserInvitationsRequest = {
    type: 'getDoesEmailHavePendingOrganizationUserInvitations';
    userEmail: string;
};

export type GetDoesEmailHavePendingOrganizationUserInvitationsResponse = {
    hasPendingInvitations: boolean;
};

export type OrganizationAvatarFileCommand = FileCommand<
    'organizationAvatar',
    {
        organizationId: string;
    }
>;

export type OrganizationLogoFileCommand = FileCommand<
    'organizationLogo',
    {
        organizationId: string;
    }
>;
export type GetOrganizationGroupDetailsRequest = {
    type: 'getOrganizationGroupDetails';
    organizationGroupId: string;
};

export type GetOrganizationGroupDetailsResponse = {
    organizationGroupDetails: APITypes.organizationService.OrganizationGroupDetails | null;
};

export type GetMaskedOrganizationDetailsRequest = {
    type: 'getMaskedOrganizationDetails';
    organizationId: string;
};

export type GetMaskedOrganizationDetailsResponse = {
    maskedOrganizationDetails: APITypes.organizationService.MaskedOrganizationDetails | null;
};

export type HandleOrganizationMedicalProfileRemovalRequest = {
    type: 'handleOrganizationMedicalProfileRemoval';
    organizationId: string;
    medicalProfileId: string;
};

export type MaybeDeleteMedicalProfileOrganizationResourcesRequest = {
    type: 'maybeDeleteMedicalProfileOrganizationResources';
    medicalProfileId: string;
};

export type GetOrganizationMedicalProfileCollaboratorForMedicalProfileAndUserRequest = {
    type: 'getOrganizationMedicalProfileCollaboratorForMedicalProfileAndUser';
    medicalProfileId: string;
    userId: string;
};

export type GetOrganizationMedicalProfileCollaboratorForMedicalProfileAndUserResponse = {
    organizationMedicalProfileCollaborator: OrganizationMedicalProfileCollaborator | null;
};

export type CreateMedicalProfileRequest = {
    type: 'createMedicalProfile';
    /**
     * The user ID of the owner of the medical profile, may be omitted if the profile is being provisioned by an organization
     */
    ownerUserId?: string;
    /**
     * The user ID of the actor creating the medical profile, may be the same or different from the owner
     */
    actorUserId: string;
    createMedicalProfileDetails: APITypes.medicalProfileService.CreateMedicalProfileRequest;
    validatePaymentStatus: boolean;
};

export type CreateMedicalProfileResponse = {
    medicalProfile: Dynamo.MedicalProfile;
    medicalProfileContactDetails: Dynamo.ContactDetails;
};

export type ProcessDailyOrganizationUsageMetricsRequest = {
    type: 'processDailyOrganizationUsageMetrics';
};

export type ProcessMonthlyOrganizationUsageMetricsRequest = {
    type: 'processMonthlyOrganizationUsageMetrics';
};

export type DeleteOrganizationRequest = {
    type: 'deleteOrganization';
    organizationId: string;
};

export type GetMaybeUserOrganizationReferenceRequest = {
    type: 'getMaybeUserOrganizationReference';
    userId: string;
    medicalProfileId: string;
};

export type GetMaybeUserOrganizationReferenceResponse = {
    organizationId: string;
} | null;

export type MaybeSaveOrganizationClientRecordAuditLogRequest = {
    type: 'maybeSaveOrganizationClientRecordAuditLog';
    auditLogMetadata: AuditLogMetadataWithPotentialOrganizationUser & {
        timestamp: number;
    };
};

export type PersistMedicalProfileFilesFromOrganizationRequest = {
    type: 'persistMedicalProfileFilesFromOrganization';
    organizationId: string;
    /**
     * Used as the destination folder name
     */
    organizationName: string;
    userId: string;
    medicalProfileIds: string[];
    tempFiles: {
        tempFileId: string;
        fileName: string;
        contentType: string;
    }[];
};

export type CopyOrganizationInvitationFilesToMedicalProfileRequest = {
    type: 'copyOrganizationInvitationFilesToMedicalProfile';
    organizationId: string;
    /**
     * Used as the destination folder name
     */
    organizationName: string;
    medicalProfileId: string;
    userId: string;
    files: {
        invitationFileId: string;
        fileName: string;
        contentType?: string;
    }[];
};

export type HandleOrganizationClientExpirationRequest = {
    type: 'handleOrganizationClientExpiration';
    expirationDateTime: string;
    organizationId: string;
    organizationClientInvitationId: string;
    organizationClientRecordId: string;
};

export type GetPublicOrganizationDetailsFromOrganizationClientInvitationRequest = {
    type: 'getPublicOrganizationDetailsFromOrganizationClientInvitation';
    organizationClientInvitationId: string;
};

export type GetPublicOrganizationDetailsFromOrganizationClientInvitationResponse = {
    organizationDetails: Pick<Organization['metadata'], 'name' | 'description'>;
} | null;

export type GetPublicOrganizationAvatarOrLogoFromOrganizationClientInvitationRequest = {
    type: 'getPublicOrganizationAvatarOrLogoFromOrganizationClientInvitation';
    avatarOrLogo: 'avatar' | 'logo';
    organizationClientInvitationId: string;
};

export type GetPublicOrganizationAvatarOrLogoFromOrganizationClientInvitationResponse = GetAvatarResponse | null;

export type GetOrganizationMedicalProfileFolderRequest = {
    type: 'getOrganizationMedicalProfileFolder';
    organizationId: string;
    medicalProfileId: string;
};

export type GetOrganizationMedicalProfileFolderResponse = {
    organizationMedicalProfileFolderId: string | null;
};

export type BatchGetMedicalProfileFilesByIdRequest = {
    type: 'batchGetMedicalProfileFilesById';
    medicalProfileId: string;
    medicalProfileFileIds: string[];
};

export type BatchGetMedicalProfileFilesByIdResponse = {
    medicalProfileFiles: Dynamo.WithTimestamps<Dynamo.MedicalProfileFile>[];
};

/**
 * Get all medical profile IDs where the user is the owner or collaborator
 */
export type GetMedicalProfileIdsForUserIdRequest = {
    type: 'getMedicalProfileIdsForUserId';
    userId: string;
};

export type GetMedicalProfileIdsForUserIdResponse = {
    /**
     * Medical profile IDs where the user is the owner
     */
    ownerMedicalProfileIds: string[];
    /**
     * Medical profile IDs where the user is a collaborator
     */
    collaboratorMedicalProfileIds: string[];
};

/**
 * Get all collaborator user IDs as well as the owner user ID for a medical profile
 */
export type GetUserIdsForMedicalProfileIdRequest = {
    type: 'getUserIdsForMedicalProfileId';
    medicalProfileId: string;
};

export type GetUserIdsForMedicalProfileIdResponse = {
    ownerUserId?: string;
    collaboratorUserIds: string[];
};

/**
 * Process a notification event, sending notifications to the appropriate users
 */
export type ProcessNotificationEventRequest<T extends NotificationType> = {
    type: 'processNotificationEvent';
    event: NotificationEvent<T>;
};

export type GetDoesUserHaveAccessToAuditLogRequest = {
    type: 'getDoesUserHaveAccessToAuditLog';
    userId: string;
    medicalProfileId: string;
    auditLogId: string;
};

export type GetDoesUserHaveAccessToAuditLogResponse = {
    hasAccess: boolean;
};

export type GetDoesUserHaveAccessToNoteRequest = {
    type: 'getDoesUserHaveAccessToNote';
    userId: string;
    medicalProfileId: string;
    noteId: string;
};

export type GetDoesUserHaveAccessToNoteResponse = {
    hasAccess: boolean;
};

export type SendProfileMessageEmailParams = {
    firstName: string;
    lastName: string;
    profile: string;
    message: string;
    notificationUrl: string;
};

export type SendProfileMessageEmailRequest = {
    type: 'sendProfileMessageEmail';
    to: string;
    params: SendProfileMessageEmailParams;
};

export type SendActivityCommentEmailParams = {
    firstName: string;
    lastName: string;
    profile: string;
    activityTitle: string;
    comment: string;
    notificationUrl: string;
};

export type SendActivityCommentEmailRequest = {
    type: 'sendActivityCommentEmail';
    to: string;
    params: SendActivityCommentEmailParams;
};

export type SendNoteCommentEmailParams = {
    firstName: string;
    lastName: string;
    profile: string;
    noteTitle: string;
    comment: string;
    notificationUrl: string;
};

export type SendNoteCommentEmailRequest = {
    type: 'sendNoteCommentEmail';
    to: string;
    params: SendNoteCommentEmailParams;
};

export type EmailAttachment = {
    type: string;
    content: string;
    name: string;
};

export type UserNoteAttachment = {
    name: string;
    content: Buffer | null;
    contentType: string;
};

export type SendVisitInviteEmailParams = {
    visitTitle: string;
    visitDateTime: string;
    profile: string;
    startDateTime: string;
    subscribers: string;
    visitDeepLink: string;
    location: string;
};

export type SendVisitInviteEmailRequest = {
    type: 'sendVisitInviteEmail';
    to: string;
    params: SendVisitInviteEmailParams;
    attachments: EmailAttachment[];
};

export type GetUserDetailsRequest = {
    type: 'getUserDetails';
    userId: string;
};

export type SendShareNoteEmailParams = {
    firstName?: string;
    lastName?: string;
    profileName?: string;
    noteTitle: string;
    noteContent: string;
    notificationUrl: string;
    senderFirstName: string;
    senderLastName: string;
    senderEmail: string;
};

export type SendShareNoteEmailRequest = {
    type: 'sendShareNoteEmail';
    to: string;
    params: SendShareNoteEmailParams;
    attachments?: AttachmentInfo[];
};

export type UserDetails = User & { emailAddress: string };

export type GetUserDetailsResponse = {
    userDetails: UserDetails;
};

export type PersistVisitsFromOrganizationRequest = {
    type: 'persistVisitsFromOrganization';
    organizationId: string;
    userId: string;
    medicalProfileIds: string[];
    /** The visit to persist; a recurrence rule expands server-side per destination profile. */
    resource?: Omit<MedicalDataTypes.PrimaryRecordVisit, 'id'>;
    images: ResourceImageCreate[];
    /** Org note to copy onto each created visit as a 'visit-resource' note. */
    sourceNoteId?: string;
};

export type GetUserMedicalProfilePreferencesRequest = {
    type: 'getUserMedicalProfilePreferences';
    userId: string;
    medicalProfileId: string;
};

export type GetUserMedicalProfilePreferencesResponse = {
    preferences: Dynamo.UserPreference[];
};

export type IncrementMedicalProfileUsageMetricRequest = {
    type: 'incrementMedicalProfileUsageMetric';
    metricType: Dynamo.MedicalProfileUsageMetricType;
    medicalProfileId: string;
    incrementBy: number;
};

export type GetIsProfilePaidRequest = {
    type: 'getIsProfilePaid';
    medicalProfileId: string;
};

export type GetIsProfilePaidResponse = {
    isPaid: boolean;
};

export type GetDoesProfileHavePaidOrganizationCollaboratorRequest = {
    type: 'getDoesProfileHavePaidOrganizationCollaborator';
    medicalProfileId: string;
};

export type GetDoesProfileHavePaidOrganizationCollaboratorResponse = {
    hasPaidOrganizationCollaborator: boolean;
};

export type GetMedicalProfileUsageMetricStatusRequest = {
    type: 'getMedicalProfileUsageMetricStatus';
    medicalProfileId: string;
    metricType: Dynamo.MedicalProfileUsageMetricType;
};

export type GetMedicalProfileUsageMetricStatusResponse = {
    used: number;
    allowed: number | 'unlimited';
};

export type ReprocessFileRequest = {
    type: 'reprocessFile';
    medicalProfileFileId: string;
    profileId: string;
};

export type GetMedicalProfileFileByIdRequest = {
    type: 'getMedicalProfileFileById';
    medicalProfileFileId: string;
};

export type GetMedicalProfileFileByIdResponse = {
    medicalProfileFile: Dynamo.MedicalProfileFile | null;
};

export type DeleteUserMedicalProfileChatsForUserRequest = {
    type: 'deleteUserMedicalProfileChatsForUser';
    userId: string;
};

export type DeleteUserMedicalProfileChatsForMedicalProfileRequest = {
    type: 'deleteUserMedicalProfileChatsForMedicalProfile';
    medicalProfileId: string;
};

export type ProcessAsyncChatMessageRequest = {
    type: 'processAsyncChatMessage';
    userId: string;
    medicalProfileId: string;
    chatId: string;
    modelMessageDateTime: number;
    text: string;
    isNewChat: boolean;
};

export type DeleteUserAuditLogsRequest = {
    type: 'deleteUserAuditLogs';
    userId: string;
};

export type ReportErrorRequest = {
    type: 'reportError';
    functionName: string;
    message?: string;
};

/**
 * For maintenance job on 9/16/2024, can be removed after
 */
export type CorrectOrganizationClientRecordClientDetailsRequest = {
    type: 'correctOrganizationClientRecordClientDetails';
    dryRun?: boolean;
};

/**
 * "delayed-re-ingest" will queue a job to re-ingest the data after a delay.  This is intended for background/batch processing
 */
export type RefreshOneUpEndpointDelayedReIngestStrategy = {
    type: 'delayed-re-ingest';
    reIngestDelay: number;
};

/**
 * "live-re-ingest" will queue many jobs within quick succession.  This is intended for a user to see updated data as soon as possible.
 */
export type RefreshOneUpEndpointLiveReIngestStrategy = {
    type: 'live-re-ingest';
};

/**
 * "no-re-ingest" will skip the re-ingest step.  This is intended for cases where the data is already up-to-date or just refreshing the on demand access token.
 */
export type RefreshOneUpEndpointNoReIngestStrategy = {
    type: 'no-re-ingest';
};

/**
 * Re-ingest strategy upon successful refresh.
 */
export type RefreshOneUpEndpointReIngestStrategy =
    | RefreshOneUpEndpointDelayedReIngestStrategy
    | RefreshOneUpEndpointLiveReIngestStrategy
    | RefreshOneUpEndpointNoReIngestStrategy;

export type RefreshOneUpEndpointRequest = {
    type: 'refreshOneUpEndpoint';
    oneUpSystemConnectionId: string;
    refreshUserId: string;
    /**
     * If true, the usage metric will not be incremented for this refresh.
     */
    skipUsageMetricCheckAndIncrement?: boolean;
    /**
     * If true, the refresh will ignore the user being disabled.
     *
     * If the purpose is to do refresh expiration discovery, for example, we want to ignore
     * the "automatic" refresh expiration check.
     */
    ignoreUserDisabledRefresh?: boolean;
    reIngestStrategy: RefreshOneUpEndpointReIngestStrategy;
};

export type UpdateOneUpEndpointAutomaticRefreshExpirationRequest = {
    type: 'updateOneUpEndpointAutomaticRefreshExpiration';
    oneUpSystemConnectionId: string;
};

export type RefreshAllOneUpEndpointsRequest = {
    type: 'refreshAllOneUpEndpoints';
};

export type RefreshAndReIngestAllOneUpUserEndpointsRequest = {
    type: 'refreshAndReIngestAllOneUpUserEndpoints';
    oneUpHealthUserId: string;
};

/**
 * Set the owner user ID for a medical profile.
 *
 * This invocation is only valid if the medical profile does not already have an owner user ID.
 * An error will be throw if the medical profile already has an owner user ID.
 */
export type SetMedicalProfileOwnerUserIdRequest = {
    type: 'setMedicalProfileOwnerUserId';
    medicalProfileId: string;
    ownerUserId: string;
};

/**
 * Adds to (does not replace) the user intents for a user.
 */
export type AddToUserIntentsRequest = {
    type: 'addToUserIntents';
    userId: string;
    userIntents: UserIntent[];
};

/**
 * Returns true if the given user belongs to an organization
 * and if the given medical profile has been provisioned by that organization.
 */
export type GetIsMedicalProfileProvisionedByUserOrganizationRequest = {
    type: 'getIsMedicalProfileProvisionedByUserOrganization';
    userId: string;
    medicalProfileId: string;
};

export type GetIsMedicalProfileProvisionedByUserOrganizationResponse = {
    isProvisionedByUserOrganization: boolean;
};

export type CreateStripeConnectedAccountForOrganizationRequest = {
    type: 'createStripeConnectedAccountForOrganization';
    userId: string;
    organizationDetails: OrganizationDetails;
    // When the organization is first set up with Stripe, we make a best effort to copy their
    // existing avatar and logo to Stripe, knowing that they mat not meet Stripe's requirements.
    // After initial setup, the Stripe images are managed independently of the organization images.
    organizationAvatar: GetAvatarResponse | null;
    organizationLogo: GetAvatarResponse | null;
    webClientType: WebStripeClientType;
};

export type CreateStripeConnectedAccountForOrganizationResponse = {
    stripeAccountId: string;
    stripeOnboardingUrl: string;
};

export type UpdateStripeConnectedAccountBrandingColorsRequest = {
    type: 'updateStripeConnectedAccountBrandingColors';
    stripeAccountId: string;
    organizationPrimaryColor?: string;
    organizationSecondaryColor?: string;
};

export type StripeConnectedAccountImageChange =
    | {
          type: 'cleared';
      }
    | {
          type: 'updated';
          imageUrl: string;
          imageName: string;
      };

export type UpdateStripeConnectedAccountBrandingImagesRequest = {
    type: 'updateStripeConnectedAccountBrandingImages';
    stripeAccountId: string;
    icon?: StripeConnectedAccountImageChange;
    logo?: StripeConnectedAccountImageChange;
};

export type UpdateStripeConnectedAccountBrandingImagesResponse = {
    icon: 'success' | 'error';
    logo: 'success' | 'error';
};

export type GetStripeConnectedAccountBrandingImageRequest = {
    type: 'getStripeConnectedAccountBrandingImage';
    stripeAccountId: string;
    imageType: 'icon' | 'logo';
};

export type GetStripeConnectedAccountBrandingImageResponse = GetAvatarResponse | null;

export type HandleStripeWebhookEventRequest = {
    type: 'handleStripeWebhookEvent';
    eventSource: StripeWebhookEventSource;
    stripeSignature: string;
    requestPayload: string;
};

export type UpdateOrganizationStripeAccountStatusRequest = {
    type: 'updateOrganizationStripeAccountStatus';
    stripeAccountId: string;
    status: OrganizationStripeAccountStatus;
};

export type GetStripeConnectedAccountDashboardUrlRequest = {
    type: 'getStripeConnectedAccountDashboardUrl';
    stripeAccountId: string;
};

export type GetStripeConnectedAccountDashboardUrlResponse = {
    stripeDashboardUrl: string;
};

export type GetStripeConnectedAccountOnboardingUrlRequest = {
    type: 'getStripeConnectedAccountOnboardingUrl';
    stripeAccountId: string;
    webClientType: WebStripeClientType;
};

export type GetStripeConnectedAccountOnboardingUrlResponse = {
    stripeOnboardingUrl: string;
};

export type DeleteStripeConnectedAccountRequest = {
    type: 'deleteStripeConnectedAccount';
    stripeAccountId: string;
};

export type CreateStripeConnectedAccountCustomerRequest = {
    type: 'createStripeConnectedAccountCustomer';
    stripeAccountId: string;
    organizationClientRecordId: string;
    email: string;
    phoneNumber?: string;
    name:
        | {
              firstName: string;
              lastName: string;
          }
        | string;
};

export type CreateStripeConnectedAccountCustomerResponse = {
    stripeCustomerId: string;
    customerDetails: {
        name: string;
        email: string;
        phoneNumber?: string;
    };
};

export type UpdateStripeConnectedAccountCustomerRequest = {
    type: 'updateStripeConnectedAccountCustomer';
    stripeAccountId: string;
    stripeCustomerId: string;
    email: string;
    name: string;
    phoneNumber?: string;
};

export type UpdateStripeConnectedAccountCustomerResponse = {
    name: string;
    email: string;
    phoneNumber?: string;
};

export type GetStripeConnectedAccountCustomerRequest = {
    type: 'getStripeConnectedAccountCustomer';
    stripeAccountId: string;
    stripeCustomerId: string;
};

export type GetStripeConnectedAccountCustomerResponse = {
    name: string;
    email: string;
    phoneNumber?: string;
} | null;

export type CreateStripeConnectedAccountCustomerInvoiceItemsRequest = {
    type: 'createStripeConnectedAccountCustomerInvoiceItems';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceItems: Omit<OrganizationInvoiceItem, 'invoiceItemId' | 'stripeCustomerId'>[];
};

export type CreateStripeConnectedAccountCustomerInvoiceItemsResponse = {
    invoiceItems: OrganizationInvoiceItem[];
};

export type ListStripeConnectedAccountCustomerInvoiceItemsRequest = {
    type: 'listStripeConnectedAccountCustomerInvoiceItems';
    stripeAccountId: string;
    stripeCustomerId: string;
};

export type ListStripeConnectedAccountCustomerInvoiceItemsResponse = {
    invoiceItems: OrganizationInvoiceItem[];
};

export type GetStripeConnectedAccountCustomerInvoiceItemRequest = {
    type: 'getStripeConnectedAccountCustomerInvoiceItem';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceItemId: string;
};

export type GetStripeConnectedAccountCustomerInvoiceItemResponse = {
    invoiceItem: OrganizationInvoiceItem | null;
};

export type UpdateStripeConnectedAccountCustomerInvoiceItemRequest = {
    type: 'updateStripeConnectedAccountCustomerInvoiceItem';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceItemId: string;
    update: Omit<OrganizationInvoiceItem, 'invoiceItemId' | 'stripeCustomerId'>;
};

export type UpdateStripeConnectedAccountCustomerInvoiceItemResponse = {
    invoiceItem: OrganizationInvoiceItem;
};

export type DeleteStripeConnectedAccountCustomerInvoiceItemRequest = {
    type: 'deleteStripeConnectedAccountCustomerInvoiceItem';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceItemId: string;
};

export type CreateStripeConnectedAccountCustomerInvoiceRequest = {
    type: 'createStripeConnectedAccountCustomerInvoice';
    stripeAccountId: string;
    stripeCustomerId: string;
    description?: string;
    daysUntilDue: number;
    userId: string;
    organizationClientRecordId: string;
};

export type CreateStripeConnectedAccountCustomerInvoiceResponse = {
    invoice: OrganizationInvoiceForStatus<'open'>;
};

export type ListStripeConnectedAccountCustomerInvoicesRequest = {
    type: 'listStripeConnectedAccountCustomerInvoices';
    stripeAccountId: string;
    stripeCustomerId: string;
};

export type ListStripeConnectedAccountCustomerInvoicesResponse = {
    invoices: OrganizationInvoice[];
};

export type GetStripeConnectedAccountCustomerInvoiceRequest = {
    type: 'getStripeConnectedAccountCustomerInvoice';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceId: string;
};

export type GetStripeConnectedAccountCustomerInvoiceResponse = {
    invoice: OrganizationInvoice | null;
};

export type ListStripeConnectedAccountCustomerInvoiceItemsForInvoiceRequest = {
    type: 'listStripeConnectedAccountCustomerInvoiceItemsForInvoice';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceId: string;
};

export type ListStripeConnectedAccountCustomerInvoiceItemsForInvoiceResponse = {
    invoiceItems: OrganizationInvoiceItem[];
};

export type VoidStripeConnectedAccountCustomerInvoiceRequest = {
    type: 'voidStripeConnectedAccountCustomerInvoice';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceId: string;
};

export type VoidStripeConnectedAccountCustomerInvoiceResponse = {
    invoice: OrganizationInvoiceForStatus<'void'>;
};

export type MarkPaidStripeConnectedAccountCustomerInvoiceRequest = {
    type: 'markPaidStripeConnectedAccountCustomerInvoice';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceId: string;
};

export type MarkPaidStripeConnectedAccountCustomerInvoiceResponse = {
    invoice: OrganizationInvoiceForStatus<'paid'>;
};

export type RefundStripeConnectedAccountCustomerInvoiceRequest = {
    type: 'refundStripeConnectedAccountCustomerInvoice';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceId: string;
    reason: 'requested_by_customer' | 'duplicate';
};

export type RefundStripeConnectedAccountCustomerInvoiceResponse = {
    invoice: OrganizationInvoiceForStatus<'refunded'>;
};

export type ResendStripeConnectedAccountCustomerInvoiceRequest = {
    type: 'resendStripeConnectedAccountCustomerInvoice';
    stripeAccountId: string;
    stripeCustomerId: string;
    invoiceId: string;
};

export type GetUserTempFileRequest = {
    type: 'getUserTempFile';
    userId: string;
    tempFileId: string;
};

export type GetUserTempFileResponse = {
    tempFile: GetAvatarResponse;
};

export type ListOrganizationClientRecordInvoicesForOrganizationIdAndMedicalProfileIdRequest = {
    type: 'listOrganizationClientRecordInvoicesForOrganizationIdAndMedicalProfileId';
    organizationId: string;
    medicalProfileId: string;
};

export type ListOrganizationClientRecordInvoicesForOrganizationIdAndMedicalProfileIdResponse = {
    invoices: OrganizationInvoiceDetails[];
};

export type GetOrganizationClientRecordInvoiceForOrganizationIdAndMedicalProfileIdRequest = {
    type: 'getOrganizationClientRecordInvoiceForOrganizationIdAndMedicalProfileId';
    medicalProfileId: string;
    organizationId: string;
    invoiceId: string;
};

export type GetOrganizationClientRecordInvoiceForOrganizationIdAndMedicalProfileIdResponse = {
    invoice: OrganizationInvoiceDetails | null;
};

/**
 * **Should only be used for testing purposes!**
 *
 * **Only works in dev**
 */
export type ClearAllStripeConnectedAccountCustomersRequest = {
    type: 'clearAllStripeConnectedAccountCustomers';
    stripeAccountId: string;
};

export type MedicalProfileDirectiveFileCommand = FileCommand<
    'medicalProfileDirectiveFile',
    {
        medicalProfileDirectiveFileId: string;
        medicalProfileId: string;
    }
>;

export type GetMedicalProfileDirectiveRequest = {
    type: 'getMedicalProfileDirective';
    medicalProfileId: string;
};

export type GetMedicalProfileDirectiveResponse = {
    medicalProfileDirective: Dynamo.MedicalProfileDirective | null;
};

export type PerformGeneralSearchRequest = {
    type: 'performGeneralSearch';
    medicalProfileId: string;
    searchQuery: string;
    searchVector: LLMTypes.Vector;
    resourceCategories: MedicalDataTypes.PrimaryRecordResourceCategory[];
    includeCareTeamMembers: boolean;
    allowedNoteEntityTypes: Dynamo.NoteEntityType[];
    allowedMedicalProfileFileIds: string[];
    maxResults: number;
};

export type PerformGeneralSearchResponse = {
    searchResults: {
        id: string;
        type: SearchTypes.Target;
    }[];
};

export type GetRegistrationCodeFileUploadUrlRequest = {
    type: 'getRegistrationCodeFileUploadUrl';
    registrationCodeId: string;
    fileId: string;
    fileName: string;
};

export type GetRegistrationCodeFileUploadUrlResponse = {
    uploadUrl: string;
};

export type DeleteRegistrationCodeFileRequest = {
    type: 'deleteRegistrationCodeFile';
    registrationCodeId: string;
    fileId: string;
};

export type GetRegistrationCodeFileMetadataRequest = {
    type: 'getRegistrationCodeFileMetadata';
    registrationCodeId: string;
    fileId: string;
};

export type GetRegistrationCodeFileMetadataResponse = FileMetadataResponse;

export type PersistMedicalProfileFileFromRegistrationCodeFileRequest = {
    type: 'persistMedicalProfileFileFromRegistrationCodeFile';
    registrationCodeId: string;
    fileId: string;
    medicalProfileId: string;
};

export type PersistMedicalProfileFileFromRegistrationCodeFileResponse = {
    medicalProfileFileId: string;
};

export type GetUserActiveRegistrationCodeFilesRequest = {
    type: 'getUserActiveRegistrationCodeFiles';
    userId: string;
};

export type GetUserActiveRegistrationCodeFilesResponse = {
    registrationCodeId: string;
    registrationCodeFiles: Dynamo.RegistrationCodeFile[];
} | null;

export type CreateMedicalProfileFilesFromRegistrationCodeFilesRequest = {
    type: 'createMedicalProfileFilesFromRegistrationCodeFiles';
    registrationCodeId: string;
    registrationCodeFiles: Dynamo.RegistrationCodeFile[];
    medicalProfileId: string;
    userId: string;
};

export type GetAllActiveOneUpEndpointsRequest = {
    type: 'getAllActiveOneUpEndpoints';
};

export type GetAllActiveOneUpEndpointsResponse = {
    endpoints: OneUpHealthEndpoint[];
};

export type GetOrganizationUsersWithAccessToMedicalProfileRequest = {
    type: 'getOrganizationUsersWithAccessToMedicalProfile';
    medicalProfileId: string;
};

export type GetOrganizationUsersWithAccessToMedicalProfileResponse = {
    organizationUserIds: string[];
};

export type GetOrganizationMedicalProfileIdsForUserIdRequest = {
    type: 'getOrganizationMedicalProfileIdsForUserId';
    userId: string;
};

export type GetOrganizationMedicalProfileIdsForUserIdResponse = {
    organizationMedicalProfileIds: string[];
};

export type GetNotificationMetadataResult<TMetadata extends NotificationMetadata> = TMetadata | 'record-not-found';

export const isNotificationMetadataRecordNotFound = <TMetadata extends NotificationMetadata>(
    result: GetNotificationMetadataResult<TMetadata>,
): result is TMetadata => {
    return result !== 'record-not-found';
};

export type GetProfileMessageNotificationMetadataRequest = {
    type: 'getProfileMessageNotificationMetadata';
    auditLogId: string;
};

export type GetProfileMessageNotificationMetadataResponse =
    GetNotificationMetadataResult<MedicalProfileMessageNotificationMetadata>;

export type GetActivityCommentNotificationMetadataRequest = {
    type: 'getActivityCommentNotificationMetadata';
    activityCommentId: string;
};

export type GetActivityCommentNotificationMetadataResponse =
    GetNotificationMetadataResult<MedicalProfileActivityCommentNotificationMetadata>;

export type GetNoteCommentNotificationMetadataRequest = {
    type: 'getNoteCommentNotificationMetadata';
    noteCommentId: string;
};

export type GetNoteCommentNotificationMetadataResponse =
    GetNotificationMetadataResult<MedicalProfileNoteCommentNotificationMetadata>;

export type GetUserUnreadInAppNotificationsForProfileRequest = {
    type: 'getUserUnreadInAppNotificationsForProfile';
    userId: string;
    medicalProfileId: string;
};

export type GetUserUnreadInAppNotificationsForProfileResponse = {
    unreadInAppNotifications: UnreadInAppNotification[];
};

export type GetUserReadInAppNotificationsForProfileRequest = {
    type: 'getUserUnreadInAppNotificationsForProfile';
    userId: string;
    medicalProfileId: string;
};

export type GetUserReadInAppNotificationsForProfileResponse = {
    readInAppNotifications: InAppNotification[];
};

export type GetAllProfileNoteCommentNotificationsMetadataRequest = {
    type: 'getAllProfileNoteCommentNotificationsMetadata';
    userId: string;
    medicalProfileId: string;
};

export type GetAllProfileNoteCommentNotificationsMetadataResponse = {
    noteCommentNotifications: MedicalProfileNoteCommentNotificationMetadata[];
};

export type GetAllProfileMessageNotificationsMetadataRequest = {
    type: 'getAllProfileMessageNotificationsMetadata';
    userId: string;
    medicalProfileId: string;
};

export type GetAllProfileMessageNotificationsMetadataResponse = {
    messageNotifications: MedicalProfileMessageNotificationMetadata[];
};

export type GetAllActivityCommentNotificationsMetadataRequest = {
    type: 'getAllActivityCommentNotificationsMetadata';
    userId: string;
    medicalProfileId: string;
};

export type GetAllActivityCommentNotificationsMetadataResponse = {
    activityCommentNotifications: MedicalProfileActivityCommentNotificationMetadata[];
};

export type GetAllUserProfileInAppNotificationsMetadataRequest = {
    type: 'getAllUserProfileInAppNotifications';
    userId: string;
    medicalProfileId: string;
};
export type GetAllUserProfileInAppNotificationsMetadataResponse = {
    allInAppNotifications: ReadOrUnreadInAppNotification[];
};

export type ToggleInAppNotificationsAsReadRequest = {
    type: 'toggleInAppNotificationsAsRead';
    userId: string;
    medicalProfileId: string;
    inAppNotificationIds: string[];
};

export type ToggleInAppNotificationsAsUnreadRequest = {
    type: 'toggleInAppNotificationsAsUnread';
    userId: string;
    medicalProfileId: string;
    readInAppNotifications: ReadInAppNotification[];
};

export type GetHasAccessToInAppNotificationAvatarRequest = {
    type: 'getHasAccessToInAppNotificationAvatar';
    notificationEntityType: NotificationTypes.NotificationType;
    medicalProfileId: string;
    /**
     * Either noteCommentId, activityCommentId, or auditLogId
     */
    notificationEntityId: string;
    requestingUserId: string;
    avatarUserId: string;
};

export type GetHasAccessToInAppNotificationAvatarResponse = {
    hasAccess: boolean;
};

/**
 * Get the number of non-validated in-app notifications for a list of profiles
 * non-validated means that the source entity may not exist anymore or the user does not
 * have access to it.  This just returns counts and not the actual notifications or source data.
 */
export type GetNonValidatedInAppNotificationCountsForProfilesRequest = {
    type: 'getNonValidatedInAppNotificationCountsForProfiles';
    userId: string;
};

export type MedicalProfileIdAndNotificationCount = {
    medicalProfileId: string;
    notificationCount: number;
};

export type GetNonValidatedInAppNotificationCountsForProfilesResponse = {
    nonValidatedNotificationCounts: MedicalProfileIdAndNotificationCount[];
};

export type AdminListedOrganization = {
    organizationId: string;
    organizationName: string;
    isPaid: boolean;
};

export type GetAdminListedOrganizationsRequest = {
    type: 'getAdminListedOrganizations';
};

export type GetAdminListedOrganizationsResponse = {
    organizations: AdminListedOrganization[];
};

export type AdminToggleOrganizationPaidStatusRequest = {
    type: 'adminToggleOrganizationPaidStatus';
    organizationId: string;
    isPaid: boolean;
    userId: string;
};

export type MaybeGetProfileCollaboratorFromEmailRequest = {
    type: 'maybeGetProfileCollaboratorFromEmail';
    medicalProfileId: string;
    email: string;
};

export type MaybeGetProfileCollaboratorFromEmailResponse = {
    firstName: string;
    preferredName: string;
    lastName: string;
} | null;

export type GetReportProfileOrganizationCollaboratorsRequest = {
    type: 'getReportProfileOrganizationCollaborators';
    medicalProfileId: string;
};

export type GetReportProfileOrganizationCollaboratorsResponse = {
    organizationCollaborators: MedicalProfileOrganizationCollaborator[];
};

export type GetReportProfileOrganizationCollaboratorLogoRequest = {
    type: 'getReportProfileOrganizationCollaboratorLogo';
    medicalProfileId: string;
    medicalProfileCollaboratorId: string;
    userId: string;
};

export type GetReportProfileOrganizationCollaboratorLogoResponse = {
    organizationLogo: FileCommandReturnValue;
};

export type ProcessFastenSuccessfulConnection = FastenWebhookConnectionSuccessEvent;
export type ProcessFastenAuthorizationRevoked = FastenWebhookAuthorizationRevokedEvent;
export type ProcessFastenSuccessfulEhiExport = FastenWebhookEhiExportSuccessEvent;
export type ProcessFastenFailedEhiExport = FastenWebhookEhiExportFailedEvent;

export type FastenResourceEntry = {
    resource: MedicalDataTypes.AllIngestedOneUpHealthResources;
    fhirVersion: MedicalDataTypes.SupportedFhirVersions;
    references?: MedicalDataTypes.ResourceReferences;
};

export type PersistFastenResources = {
    type: 'persistFastenResources';
    medicalProfileId: string;
    orgConnectionId: string;
    platformName: string | null;
    institutionName: string | null;
    fastenResources: FastenResourceEntry[];
};

/**
 * Request to refresh a single Fasten connection
 */
export type RefreshFastenConnectionRequest = {
    type: 'refreshFastenConnection';
    medicalProfileId: string;
    orgConnectionId: string;
    refreshUserId: string;
    /**
     * If true, the usage metric will not be incremented for this refresh.
     */
    skipUsageMetricCheckAndIncrement?: boolean;
};

export type RefreshFastenConnectionResponse = {
    success: boolean;
    fastenRefreshProgressId?: string;
    taskId?: string;
    errorMessage?: string;
};

/**
 * Request to refresh all Fasten connections (for scheduled job)
 */
export type RefreshAllFastenConnectionsRequest = {
    type: 'refreshAllFastenConnections';
};

/**
 * Request to re-create clinical Notes for one profile by REPLAYING an already-archived Fasten export from S3
 * (no new Fasten pull). Reads the archived NDJSON for (medicalProfileId, taskId) and re-runs the note-creation
 * path; persistence is intentionally NOT re-run (visits already exist). Idempotent.
 */
export type ReprocessFastenArchiveRequest = {
    type: 'reprocessFastenArchive';
    medicalProfileId: string;
    taskId: string;
};

/**
 * Analytics events submitted by trusted backend services. `backend` is added to the platform union for
 * server-side events that have no client, and `userId` may be supplied directly because the caller is
 * another Lambda rather than an end user. Written longhand rather than derived from
 * `APITypes.analyticsService.AnalyticsEventInput` so that widening the client contract can never
 * silently widen the trusted one.
 */
export type AnalyticsProducerPlatform = APITypes.analyticsService.AnalyticsClientPlatform | 'backend';

export type TrustedAnalyticsEventInput = {
    eventId: string;
    eventType: string;
    eventTimestamp: string;
    platform: AnalyticsProducerPlatform;
    session?: APITypes.analyticsService.AnalyticsEventSession;
    appVersion?: string;
    appBuild?: string;
    osVersion?: string;
    /** Client software identity — on web, the browser name and version. Never a hardware identifier. */
    deviceMake?: string;
    deviceModel?: string;
    attributes?: Record<string, string>;
    /** Trusted Lambda-to-Lambda callers only; unreachable from the HTTP contract. */
    userId?: string;
};

export type RecordAnalyticsEventsRequest = {
    type: 'recordAnalyticsEvents';
    events: TrustedAnalyticsEventInput[];
};

export type RecordAnalyticsEventsResponse = {
    accepted: number;
    rejected: number;
};
